import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

import { useDebounce } from '@uidotdev/usehooks';
import { PDFDocumentProxy } from 'pdfjs-dist';
import { useEffect, useState } from 'react';
import { Document, Page } from 'react-pdf';

import { getSampleTemplateValuesAsStrings } from '@/components/template-editor/data/SampleTemplateContext';
import { enhancedApi as api } from '@/services/api/documentGenerationService';

import { computePdfMergeFields } from '../../hooks/mergeFields';
import { DocumentLoadingState } from '../../templates/components/DocumentLoadingState';
import { PdfFormTemplateField } from '../../types';
import { modifyPdf } from './utils';

interface PdfFormRendererProps {
  file: File | Blob;
  placedFields: PdfFormTemplateField[];
  renderWidth?: number;
  renderHeight?: number;
  maxPages?: number;
}

export const PdfRenderer = ({
  file,
  renderWidth,
  renderHeight,
  maxPages,
}: {
  file: File | Blob;
  renderWidth?: number;
  renderHeight?: number;
  maxPages?: number;
}) => {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const totalPages = pdf?.numPages || 0;
  const visiblePages = maxPages ? Math.min(totalPages, maxPages) : totalPages;

  return (
    <div className="w-full">
      <Document
        file={file}
        onLoadSuccess={setPdf}
        className="flex flex-col items-center"
      >
        {Array.from(new Array(visiblePages), (_el, index) => (
          <Page
            className="relative mb-4 shadow-lg"
            pageNumber={index + 1}
            key={`page_${index + 1}`}
            width={renderWidth}
            height={renderHeight}
          />
        ))}
      </Document>
    </div>
  );
};

export const PdfFormRenderer = ({
  file,
  placedFields,
  renderWidth,
  renderHeight,
  maxPages,
}: PdfFormRendererProps) => {
  const [modifiedPdfBlob, setModifiedPdfBlob] = useState<Blob | null>(null);
  const [normalizePdf, { data: normalizedPdfBlob, isLoading }] =
    api.usePdfTemplatesNormalizePdfMutation();

  useEffect(() => {
    const fetchPdf = async () => {
      await normalizePdf({
        normalizePdfSchema: { pdf: file },
      });
    };
    fetchPdf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]); // Removed normalizePdf from dependencies (RTK Query mutations are stable)

  const debouncedFields = useDebounce(placedFields, 150);

  useEffect(() => {
    let isActive = true;

    const mergeData = computePdfMergeFields(
      debouncedFields,
      getSampleTemplateValuesAsStrings(),
      {},
    );

    const _modifyPdf = async (pb: Blob) => {
      const blob = await modifyPdf({
        pdfBlob: pb,
        placedFields: debouncedFields,
        mergeData,
      });
      if (isActive) {
        setModifiedPdfBlob(blob);
      }
    };

    if (normalizedPdfBlob) {
      _modifyPdf(normalizedPdfBlob as Blob);
    }

    return () => {
      isActive = false;
    };
  }, [normalizedPdfBlob, debouncedFields]);

  // Show a single, consistent loading state until we have a fully-renderable PDF.
  // This prevents multiple mount/unmount cycles (and the associated flicker) that
  // occurred when we had separate states for "isLoading" vs "!modifiedPdfBlob".
  if (isLoading || !modifiedPdfBlob)
    return (
      <DocumentLoadingState
        message={isLoading ? 'Loading PDF template...' : 'Preparing PDF preview...'}
        variant="document"
        showSkeleton={false}
      />
    );

  return (
    <div>
      <PdfRenderer
        file={modifiedPdfBlob}
        renderWidth={renderWidth}
        renderHeight={renderHeight}
        maxPages={maxPages}
      />
    </div>
  );
};
